import webApiAdmin from '@/lib/api-common/webApiAdmin';
import { createCachedApi } from '@/lib/utils/indexedDbCache';

/* ── panel.* 엔드포인트 ── */

/**
 * Load a panel user (+ sibling users).
 * - Legacy: POST /api3/panel/loadUser
 * - New:    POST /admin/panel/load-user
 */
export async function loadPanelUser<T = unknown>(userId: string | number): Promise<T> {
  return webApiAdmin.post<T>('admin/panel/load-user', { userId: String(userId) });
}

/**
 * Save a panel user (full payload).
 * - Legacy: POST /api3/panel/saveUser
 * - New:    POST /admin/panel/save-user
 */
export async function savePanelUser(params: { userId: string | number } & Record<string, unknown>): Promise<void> {
  await webApiAdmin.post('admin/panel/save-user', { ...params, userId: String(params.userId) });
}

/**
 * Save the overview tab (demographic + rates).
 * - Legacy: POST /api3/panel/saveOverview
 * - New:    POST /admin/panel/save-overview ({ userId, data })
 * Call-sites already send { userId, data } — passthrough.
 */
export async function savePanelOverview(params: { userId: string | number } & Record<string, unknown>): Promise<void> {
  await webApiAdmin.post('admin/panel/save-overview', { ...params, userId: String(params.userId) });
}

/**
 * Load panel stats.
 * - Legacy: POST /api3/panel/loadStats
 * - New:    POST /admin/panel/load-stats
 *
 * Cached (5min TTL, IndexedDB) — the nav's Statistics count badge prefetches
 * this in the background (to compute the same per-platform row counts the
 * Stats tab itself renders) alongside StatsTab's own on-open fetch, which
 * then hits the same cache instead of the network.
 */
const _cachedPanelStats = createCachedApi<unknown>({
  prefix: 'panel.loadStats::',
  fetcher: (params) => webApiAdmin.post('admin/panel/load-stats', params),
});
export async function loadPanelStats<T = unknown>(userIds: (string | number)[]): Promise<T> {
  return _cachedPanelStats.fetch({ userIds }) as Promise<T>;
}
export const clearPanelStatsCache = _cachedPanelStats.clear;

/**
 * Load panel campaigns.
 * - Legacy: POST /api3/panel/loadCampaigns
 * - New:    POST /admin/panel/load-campaigns
 *
 * Cached (5min TTL, IndexedDB) — call-sites request different subsets of the
 * same response shape via `T` (ChannelsTab wants `campaigns`, StatsTab wants
 * `records`+`campaigns`), but it's one fixed HTTP response either way, so a
 * single cache entry per `userIds` set serves every caller/T combination.
 */
const _cachedPanelCampaigns = createCachedApi<unknown>({
  prefix: 'panel.loadCampaigns::',
  fetcher: (params) => webApiAdmin.post('admin/panel/load-campaigns', params),
});
export async function loadPanelCampaigns<T = unknown>(userIds: (string | number)[]): Promise<T> {
  return _cachedPanelCampaigns.fetch({ userIds }) as Promise<T>;
}
export const clearPanelCampaignsCache = _cachedPanelCampaigns.clear;

/**
 * Load channel live streams.
 * - Legacy: POST /api3/panel/loadChannelLiveStreams → { streams }
 * - New:    POST /admin/panel/load-channel-live-streams
 *
 * Cached (5min TTL, IndexedDB) — also backs the panel nav's Live Streams
 * count badge, which prefetches this in the background alongside the tab's
 * own on-open fetch; the cache means the second call is free.
 */
const _cachedChannelLiveStreams = createCachedApi<unknown>({
  prefix: 'panel.loadChannelLiveStreams::',
  fetcher: (params) => webApiAdmin.post('admin/panel/load-channel-live-streams', params),
});
export async function loadChannelLiveStreams<T = unknown>(userId: string | number): Promise<T> {
  return _cachedChannelLiveStreams.fetch({ userId: String(userId) }) as Promise<T>;
}
export const clearChannelLiveStreamsCache = _cachedChannelLiveStreams.clear;

/**
 * Set the price of a single video (post) in the stats tab.
 * - Legacy: POST /api3/campaigns/setVideoPrice
 * - New:    POST /admin/campaign/video-price/set
 */
export async function setVideoPrice<T = unknown>(
  userId: string | number,
  campaignId: string,
  id: string,
  price: number,
): Promise<T> {
  return webApiAdmin.post<T>('admin/campaign/video-price/set', { userId, campaignId, id, price });
}

/**
 * Load panel payments.
 * - Legacy: POST /api3/panel/loadPayments
 * - New:    POST /admin/panel/load-payments
 *
 * Cached (5min TTL, IndexedDB) — the nav's Payments count badge prefetches
 * this in the background; PaymentsTab's own on-open fetch then hits cache.
 */
const _cachedPanelPayments = createCachedApi<unknown>({
  prefix: 'panel.loadPayments::',
  fetcher: (params) => webApiAdmin.post('admin/panel/load-payments', params),
});
export async function loadPanelPayments<T = unknown>(userIds: (string | number)[]): Promise<T> {
  return _cachedPanelPayments.fetch({ userIds }) as Promise<T>;
}
export const clearPanelPaymentsCache = _cachedPanelPayments.clear;

/**
 * Add a sponsored video.
 * - Legacy: POST /api3/panel/addSponsoredVideo
 * - New:    POST /admin/panel/add-sponsored-video
 */
export async function addSponsoredVideo(userId: string | number, url: string): Promise<void> {
  await webApiAdmin.post('admin/panel/add-sponsored-video', { userId: String(userId), url });
}

/**
 * Delete a sponsored video.
 * - Legacy: POST /api3/panel/deleteSponsoredVideo
 * - New:    POST /admin/panel/delete-sponsored-video
 */
export async function deleteSponsoredVideo(userId: string | number, id: string): Promise<void> {
  await webApiAdmin.post('admin/panel/delete-sponsored-video', { userId: String(userId), id });
}

/**
 * Set a limited (permission-gated) field.
 * - Legacy: POST /api3/panel/setLimitedField
 * - New:    POST /admin/panel/set-limited-field
 */
export async function setPanelLimitedField(
  userId: string | number,
  fieldName: string,
  value: unknown,
): Promise<void> {
  await webApiAdmin.post('admin/panel/set-limited-field', { userId: String(userId), fieldName, value });
}

/**
 * Set the default reason for recommendation.
 * - Legacy: POST /api3/panel/setDefaultReasonForRecommendation
 * - New:    POST /admin/panel/set-default-reason-for-recommendation
 */
export async function setDefaultReasonForRecommendation(
  userId: string | number,
  value: string,
  applyToSiblings?: boolean,
): Promise<void> {
  await webApiAdmin.post('admin/panel/set-default-reason-for-recommendation', { userId: String(userId), value, applyToSiblings });
}

/**
 * Save tags for a user's channel.
 * - Legacy: POST /api3/channels/saveTags
 * - New:    POST /admin/channels/save-tags
 */
export async function setPanelTags(userId: string | number, tags: string[]): Promise<void> {
  await webApiAdmin.post('admin/channels/save-tags', { userId: String(userId), tags });
}

/**
 * Load AI-recommended tags for a user's channel (silent — no error alert).
 * - Legacy: POST /api3/channels/loadRecommendedTags
 * - New:    POST /admin/channels/load-recommended-tags
 */
export async function loadSuggestedTags(userId: string | number): Promise<{ tags?: string[] }> {
  const res = await webApiAdmin.postSilent<{ tags?: string[] }>(
    'admin/channels/load-recommended-tags',
    { userId: String(userId) },
  ).catch(() => ({}));
  return res ?? {};
}

/**
 * Load all tags ever used in the org (for autocomplete dropdown, silent).
 * - Legacy: POST /api3/channels/loadOldTags
 * - New:    POST /admin/channels/load-old-tags
 */
export async function loadOldTags(): Promise<string[]> {
  const res = await webApiAdmin.postSilent<string[]>('admin/channels/load-old-tags').catch(() => []);
  return Array.isArray(res) ? res : [];
}

/**
 * Load recently used tags across the organization (silent).
 * - Legacy: POST /api3/channels/loadRecentlyUsedTags
 * - New:    POST /admin/channels/load-recently-used-tags
 */
export async function loadRecentlyUsedTags(): Promise<string[]> {
  const res = await webApiAdmin.postSilent<string[]>('admin/channels/load-recently-used-tags').catch(() => []);
  return Array.isArray(res) ? res : [];
}

/**
 * Save influencer channel info (payment, social links, country, blacklist, memo).
 * - Legacy: POST /api3/channels/saveChannel
 * - New:    POST /admin/channels/save
 *
 * No page-level caller yet — wrapper ready for the channel editing UI.
 */
export async function saveChannelInfo(params: {
  userId: string;
  paypalEmail?: string;
  country?: string;
  memo?: string;
  isBlacklisted?: boolean;
  [key: string]: unknown;
}): Promise<void> {
  await webApiAdmin.post('admin/channels/save', { ...params, userId: String(params.userId) });
}

/**
 * Pre-save check for channel fields (e.g. PayPal email conflict detection).
 * - Legacy: POST /api3/channels/presaveCheck
 * - New:    POST /admin/channels/presave-check
 *
 * No page-level caller yet — wrapper ready for the channel editing UI.
 */
export async function presaveChannelCheck(params: {
  userId: string;
  paypalEmail?: string;
}): Promise<Record<string, unknown>> {
  return webApiAdmin.post<Record<string, unknown>>('admin/channels/presave-check', { ...params, userId: String(params.userId) });
}

/**
 * Load available channel categories.
 * - Legacy: POST /api3/channels/loadCategories
 * - New:    POST /admin/channels/load-categories
 *
 * No page-level caller yet — wrapper ready for the channel editing UI.
 */
export async function loadChannelCategories(): Promise<unknown[]> {
  const res = await webApiAdmin.post<unknown[]>('admin/channels/load-categories');
  return Array.isArray(res) ? res : [];
}

/**
 * Trigger an on-demand TikTok/Instagram channel stats refresh (net-new v2
 * feature, no legacy equivalent). `userId` must be the channel-owning User
 * doc's own id (the doc whose `channels[0].type` is tiktok/instagram), not
 * necessarily the currently-viewed panel user.
 * - New: POST /admin/channels/update-instantly
 * Poll the result via `getAsyncResult` (src/lib/api/async-request.api.ts).
 */
export async function updateChannelInstantly(
  userId: string | number,
): Promise<{ requestId: string; timeoutAt: string }> {
  return webApiAdmin.post('admin/channels/update-instantly', { userId: String(userId) });
}

/* ── webApi2 profile 관련 ── */

/**
 * Load the user profile (the influencer detail page).
 * - Legacy: POST /api2/user_profile_get → returns the User document directly.
 * - New:    POST /admin/user-profile/get → returns `{ user, posts, sharableLinks }`.
 *   Callers historically expect the user at the top level, so we unwrap `.user`
 *   and attach `posts`/`sharableLinks` to keep them accessible without churn.
 */
export async function getUserProfile(userId: string | number): Promise<unknown> {
  const data = await webApiAdmin.post<{
    user?: Record<string, unknown>;
    posts?: unknown[];
    sharableLinks?: unknown[];
  }>('admin/user-profile/get', { userId: String(userId) });
  if (data?.user) {
    return { ...data.user, posts: data.posts, sharableLinks: data.sharableLinks };
  }
  return data;
}

/**
 * Count work-orders per campaign for the user.
 * - Legacy: GET /api2/user_campaign_work_order_counts/{userId} → `{ [campaignId]: { new?, accepted? } }`.
 * - New:    POST /admin/user-profile/work-order-counts → `Array<{ campaignId, byStatus: { new, accepted, declined, canceled } }>`.
 *
 * FE callers (e.g. influencers/profile.tsx) expect record-keyed shape; flatten
 * the new array response into that form so the consumer stays untouched.
 */
export async function getUserCampaignWorkOrderCounts(userId: string | number): Promise<unknown> {
  const list = await webApiAdmin.post<Array<{
    campaignId: string;
    byStatus?: { new?: number; accepted?: number };
  }>>('admin/user-profile/work-order-counts', { userId: String(userId) });
  const record: Record<string, { new?: number; accepted?: number }> = {};
  for (const row of list ?? []) {
    if (row?.campaignId) record[row.campaignId] = row.byStatus ?? {};
  }
  return record;
}

/**
 * Load user-profile stats for a specific tab.
 * - POST /admin/user-profile/stats/{action} { userId }.
 * Supported actions (Swagger): videos, messages, statistics, payments,
 * comparison, updates — the profile UI only ever requests these.
 *
 * Cached (5min TTL, IndexedDB), keyed on `{userId, statsAction}` — the Stats
 * sub-tab nav's count badges prefetch every action in the background as soon
 * as the profile loads; `reloadStats` (fired when a tab is actually opened)
 * hits the same cache, so the prefetch doesn't cost a second network call.
 */
const _cachedUserProfileStats = createCachedApi<unknown>({
  prefix: 'panel.userProfileStats::',
  fetcher: (params) => webApiAdmin.post(`admin/user-profile/stats/${params.statsAction}`, { userId: params.userId }),
});
export async function getUserProfileStats(
  userId: string | number,
  statsAction: string,
): Promise<unknown> {
  return _cachedUserProfileStats.fetch({ userId: String(userId), statsAction });
}
export const clearUserProfileStatsCache = _cachedUserProfileStats.clear;

/**
 * Refresh a user's stats (queue background task) — v2 pending, no BE v2 endpoint.
 * The legacy `POST /api3/tasks/runTask` was removed for v2-only; the "refresh
 * stats" button is already hidden behind `SHOW_NO_V2_FEATURES` on the profile.
 * RESTORE: await webApiAdmin.post('admin/tasks/run', { userId: String(userId), task });
 * (or a dedicated profile refresh endpoint). Original: POST /api3/tasks/runTask.
 */
export async function runUserStatTask(_userId: string | number, _task: string): Promise<void> {
  // no-op until a v2 task-run endpoint exists
}

/**
 * Create/save a payment for an influencer.
 * - Legacy: POST /api2/payment_save
 * - New:    POST /admin/payments/save
 */
export async function savePayment(params: Record<string, unknown>): Promise<unknown> {
  return webApiAdmin.post('admin/payments/save', params);
}

/**
 * Send a Twitter DM — v2 pending, no BE v2 endpoint (admin-message `_notifyTwitter`
 * is a no-op placeholder). Legacy `POST /api2/send_twitter_message` removed for
 * v2-only; the button is already hidden behind `SHOW_NO_V2_FEATURES` on the profile.
 * RESTORE: return webApiAdmin.post('admin/panel/send-twitter-message', params);
 */
export async function sendTwitterMessage(_params: Record<string, unknown>): Promise<unknown> {
  // no-op until a v2 twitter-DM endpoint exists
  return undefined;
}

/**
 * Upload a file to the outbound attachment library and return its public URL.
 * - POST /admin/upload/file (multipart `file`)
 *
 * ⚠️ Launch prerequisite: on production, CloudFront must route /api-v2/* to the
 * production BE or this call receives the SPA's index.html → "Invalid response
 * body". Verified working on staging/dev via the Vite proxy.
 */
export async function uploadFile(params: { file: File }): Promise<{ url: string; filename: string; fileId: string; type: string; size: number }> {
  return webApiAdmin.postFile("admin/upload/file", { file: params.file });
}

/**
 * Save the invoice URL on an influencer's campaign entry.
 * - Legacy: POST /api2/update_invoice
 * - New:    POST /admin/campaign/invoice/update
 */
export async function updateInvoice(params: { userId: string; campaignId: string; invoiceUrl: string }): Promise<unknown> {
  return webApiAdmin.post('admin/campaign/invoice/update', params);
}

/**
 * Viewership time-series — v2 pending, no BE v2 endpoint. Legacy
 * `POST /api3/stats/loadViewership` removed for v2-only; the widget is already
 * hidden behind `SHOW_NO_V2_FEATURES` on the profile.
 * RESTORE: return (await webApiAdmin.post<ViewershipRecord[]>('admin/stats-v2/viewership', { userId })) ?? [];
 */
export async function loadViewership(_userId: string): Promise<ViewershipRecord[]> {
  return [];
}

export interface ViewershipRecord {
  _id?: string;
  userId?: string;
  allTime?: {
    subscribers?: number;
    posts?: number;
    views?: number;
    likes?: number;
    comments?: number;
  };
  createdAt?: string;
}

/**
 * Save a link.
 * - Legacy: POST /api2/links_save
 * - New:    POST /admin/links/save
 */
export async function saveLink(params: Record<string, unknown>): Promise<unknown> {
  return webApiAdmin.post('admin/links/save', params);
}

/**
 * Remove a link.
 * - Legacy: POST /api2/links_remove
 * - New:    POST /admin/links/remove
 */
export async function removeLink(params: Record<string, unknown>): Promise<unknown> {
  return webApiAdmin.post('admin/links/remove', params);
}

/**
 * Apply a link to a video (post).
 * - Legacy: POST /api2/links_apply
 * - New:    POST /admin/links/apply
 */
export async function applyLink(params: Record<string, unknown>): Promise<unknown> {
  return webApiAdmin.post('admin/links/apply', params);
}
